// Hand-rolled fetch wrappers for the /project-suggestions endpoints.

import { getImpersonationHeaders } from "./impersonation";

export interface ProjectSuggestionTask {
  id: string;
  title: string;
  description: string | null;
}

export interface ProjectSuggestion {
  id: string;
  name: string;
  description: string | null;
  reason: string | null;
  confidence: number;
  taskIds: string[];
  tasks: ProjectSuggestionTask[];
  createdAt: string;
}

export interface AnalyzeResult {
  ok: boolean;
  created: number;
  groupsProposed: number;
  skippedDuplicate: number;
  skippedDismissed: number;
  reason?:
    | "ok"
    | "queued"
    | "already_running"
    | "not_enough_tasks"
    | "ai_cap_reached"
    | "ai_failed"
    | "no_groups";
}

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url, {
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...getImpersonationHeaders(),
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  if (!r.ok) {
    let msg = `HTTP ${r.status}`;
    try {
      const body = await r.json();
      if (typeof body?.error === "string") msg = body.error;
      else if (typeof body?.message === "string") msg = body.message;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return (await r.json()) as T;
}

export const listProjectSuggestions = () =>
  jsonFetch<{ suggestions: ProjectSuggestion[] }>("/api/project-suggestions");

export const analyzeProjectSuggestions = () =>
  jsonFetch<AnalyzeResult>("/api/project-suggestions/analyze", {
    method: "POST",
    body: "{}",
  });

export const acceptProjectSuggestion = (
  id: string,
  body: { name?: string; description?: string | null; taskIds?: string[] },
) =>
  jsonFetch<{
    ok: boolean;
    project: { id: string; name: string; description: string | null };
    attachedTaskCount: number;
  }>(`/api/project-suggestions/${id}/accept`, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const dismissProjectSuggestion = (id: string) =>
  jsonFetch<{ ok: boolean }>(`/api/project-suggestions/${id}/dismiss`, {
    method: "POST",
    body: "{}",
  });
